import { readFailureDetail, type ApiFetch } from './client.ts'

// Client for the Curri-internal lookup behind the ghost control (INT-402).
//
// The endpoint is undocumented (`hide: true` in the service's surfaces/rest/
// admin.ts) and admin-only, so it is deliberately not part of the partner client
// in webhooks.ts and does not share its `ApiFailure` union: the outcomes here are
// "found", "no such user", and "you are not an admin", none of which any partner
// screen renders.

/** The user a Curri admin is viewing the dashboard as. */
export interface GhostTarget {
  userId: number
  externalId: string | null
  emailAddress: string | null
  /** Full name when the user has one on file. */
  name: string | null
}

// Unexported: every caller narrows on `ok` at the call site rather than naming
// this, and the repo's dead-code gate runs against an empty baseline, so an
// export nothing imports is a CI failure. Export it when something needs to name
// it.
type UserLookup =
  | { ok: true; target: GhostTarget }
  | { ok: false; message: string }

const MESSAGES: Record<number, string> = {
  403: 'Viewing as another user is restricted to Curri administrators.',
  404: 'No Curri user matches that id, externalId, or email address.',
}

// The service answers failures as RFC 9457 problem documents, whose `detail` is
// written for a human — so prefer it, and fall back to a per-status line only when
// the body is missing or unparseable (a proxy error page, an aborted body).
const messageFor = async (response: Response): Promise<string> => {
  const detail = await readFailureDetail(response)
  if (detail) return detail

  return (
    MESSAGES[response.status] ??
    `That lookup failed (HTTP ${response.status}). Try again shortly.`
  )
}

export const lookupUser = async (
  apiFetch: ApiFetch,
  identifier: string
): Promise<UserLookup> => {
  let response: Response
  try {
    response = await apiFetch(
      `/api/admin/users?identifier=${encodeURIComponent(identifier)}`
    )
  } catch {
    return {
      ok: false,
      message: 'Could not reach Curri. Check your connection.',
    }
  }

  if (!response.ok) return { ok: false, message: await messageFor(response) }

  const body = (await response.json()) as { success: true; data: GhostTarget }
  return { ok: true, target: body.data }
}
